import React, { useMemo, useState, useEffect } from "react";
import {
  Layout,
  Menu,
  Button,
  Drawer,
  Typography,
  Space,
  Grid,
  ConfigProvider,
  theme,
  Dropdown,
  Avatar,
  Divider,
} from "antd";
import {
  FaAngleLeft,
  FaBars,
  FaBed,
  FaCertificate,
  FaCog,
  FaHome,
  FaHotel,
  FaIdBadge,
  FaInfoCircle,
  FaPeopleArrows,
  FaQuestion,
  FaSmile,
  FaTable,
  FaTachometerAlt,
  FaTags,
  FaUsers,
  FaVrCardboard,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import ManageCoupon from "./ManageCoupons";
import ManageRegistrationsV2 from "./ManageRegistrationsV2";
import ManageSpeakers from "./ManageSpeakers";
import ManageCommittees from "./ManageCommittee";
import ManageAccommodationAndTravel from "./ManageAccommodationAndTravel";
import ManageFAQs from "./ManageFAQ";
import ManageHotelBookings from "./ManageHotelBookings";
import Badges from "./Badges";
import ManageRooms from "./ManageRooms";
import Settings from "./Settings";
import PanelSurveyDashboard from "./PanelSurveyDashboard";
import ManageECertificates from "./ManageECertificates";
import DashboardStats from "./DashboardStats";

const { Sider, Header, Content } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

type ViewKey =
  | "stats"
  | "orders"
  | "ecertificates"
  | "surveys"
  | "hotelBookings"
  | "coupons"
  | "speakers"
  | "committees"
  | "accommodation"
  | "badges"
  | "rooms"
  | "faqs"
  | "settings";

const viewMeta: Record<ViewKey, { title: string; subtitle?: string }> = {
  stats: {
    title: "Dashboard",
    subtitle: "Manage participants, status, notes, exports",
  },
  orders: {
    title: "Registrations",
    subtitle: "Manage participants, status, notes, exports",
  },
  ecertificates: {
    title: "E-Certificate Requests",
    subtitle: "Approve and manage certificate requests",
  },
  surveys: { title: "Surveys", subtitle: "View survey insights and responses" },
  hotelBookings: {
    title: "Hotel Bookings",
    subtitle: "Reservations, guests, occupancy, notes",
  },
  coupons: { title: "Coupons", subtitle: "Discount codes and rules" },
  speakers: { title: "Speakers", subtitle: "Speaker profiles and lineup" },
  committees: { title: "Committees", subtitle: "Committee members and roles" },
  accommodation: {
    title: "Accommodation & Travel",
    subtitle: "Hotel info, travel guides, and content",
  },
  badges: { title: "Print Badges", subtitle: "Badge printing and templates" },
  rooms: {
    title: "Hotel Rooms",
    subtitle: "Room limits, booked counts, availability",
  },
  faqs: { title: "FAQs", subtitle: "Manage frequently asked questions" },
  settings: { title: "Settings", subtitle: "Admin settings and configuration" },
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const screens = useBreakpoint();

  const isMobile = !screens.lg; // <= 992px
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const [currentView, setCurrentView] = useState<ViewKey>("orders");

  useEffect(() => {
    const isAuthenticated = localStorage.getItem("isAuthenticated");
    if (!isAuthenticated) navigate("/login");
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("isAuthenticated");
    navigate("/login");
  };

  const menuItems = useMemo(
    () => [
      {
        key: "stats",
        icon: <FaTachometerAlt />,
        label: "Dashboard",
      },
      { key: "orders", icon: <FaTable />, label: "Registrations" },
      {
        key: "ecertificates",
        icon: <FaCertificate />,
        label: "E-Cert Requests",
      },
      { key: "surveys", icon: <FaSmile />, label: "Surveys" },
      { key: "hotelBookings", icon: <FaHotel />, label: "Hotel Bookings" },
      { key: "coupons", icon: <FaTags />, label: "Coupons" },
      { key: "speakers", icon: <FaUsers />, label: "Speakers" },
      { key: "committees", icon: <FaPeopleArrows />, label: "Committees" },
      {
        key: "accommodation",
        icon: <FaInfoCircle />,
        label: "Accommodation & Travel",
      },
      { key: "badges", icon: <FaIdBadge />, label: "Print Badges" },
      { key: "rooms", icon: <FaBed />, label: "Hotel Rooms" },
      { key: "faqs", icon: <FaQuestion />, label: "FAQs" },
      { key: "settings", icon: <FaCog />, label: "Settings" },
    ],
    [],
  );

  const renderView = () => {
    switch (currentView) {
      case "stats":
        return <DashboardStats onNavigate={(key) => setCurrentView(key)} />;
      case "orders":
        return <ManageRegistrationsV2 />;
      case "ecertificates":
        return <ManageECertificates />;
      case "surveys":
        return <PanelSurveyDashboard />;
      case "hotelBookings":
        return <ManageHotelBookings />;
      case "coupons":
        return <ManageCoupon />;
      case "speakers":
        return <ManageSpeakers />;
      case "committees":
        return <ManageCommittees />;
      case "accommodation":
        return <ManageAccommodationAndTravel />;
      case "badges":
        return <Badges />;
      case "rooms":
        return <ManageRooms />;
      case "faqs":
        return <ManageFAQs />;
      case "settings":
        return <Settings />;
      default:
        return null;
    }
  };

  const menu = (
    <Menu
      mode="inline"
      selectedKeys={[currentView]}
      onClick={(info) => {
        setCurrentView(info.key as ViewKey);
        if (isMobile) setDrawerVisible(false);
      }}
      items={menuItems.map((item) => ({
        key: item.key,
        icon: item.icon,
        label: item.label,
      }))}
      style={{
        border: "none",
        background: "transparent",
        padding: 8,
      }}
    />
  );

  const userMenu = (
    <Menu
      items={[
        {
          key: "settings",
          label: "Settings",
          onClick: () => setCurrentView("settings"),
        },
        {
          key: "logout",
          label: "Logout",
          danger: true,
          onClick: handleLogout,
        },
      ]}
    />
  );

  return (
    <ConfigProvider
      theme={{
        algorithm: theme.defaultAlgorithm,
        token: {
          colorPrimary: "#2F54EB",
          borderRadius: 14,
          fontSize: 14,
        },
        components: {
          Layout: {
            headerBg: "rgba(255,255,255,0.80)",
            bodyBg: "transparent",
          },
          Menu: {
            itemBorderRadius: 12,
            itemHeight: 44,
            itemMarginInline: 8,
            itemMarginBlock: 6,
            colorItemBgSelected: "rgba(47,84,235,0.10)",
            colorItemTextSelected: "#2F54EB",
          },
          Button: {
            borderRadius: 12,
          },
          Drawer: {
            colorBgElevated: "#fff",
          },
        },
      }}
    >
      <Layout
        style={{
          minHeight: "100vh",
          background:
            "radial-gradient(1200px 600px at 0% 0%, rgba(47,84,235,0.10), transparent 60%), radial-gradient(1000px 500px at 100% 0%, rgba(82,196,26,0.08), transparent 55%), #F5F7FB",
        }}
      >
        {/* Desktop Sider */}
        {!isMobile && (
          <Sider
            width={280}
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
            trigger={null}
            style={{
              margin: 16,
              borderRadius: 18,
              background: "rgba(255,255,255,0.75)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(15, 23, 42, 0.06)",
              overflow: "hidden",
              boxShadow: "0 12px 30px rgba(15,23,42,0.06)",
            }}
          >
            {/* Brand */}
            <div style={{ padding: collapsed ? 16 : 18 }}>
              <Space align="center" size={12}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 14,
                    background:
                      "linear-gradient(135deg, rgba(47,84,235,1), rgba(47,84,235,0.55))",
                    boxShadow: "0 10px 24px rgba(47,84,235,0.25)",
                  }}
                />
                {!collapsed && (
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      Webmaster
                    </Title>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Dashboard
                    </Text>
                  </div>
                )}
              </Space>
            </div>

            <Divider style={{ margin: 0, opacity: 0.6 }} />

            {/* Menu */}
            <div style={{ padding: 8 }}>{menu}</div>

            {/* Bottom actions */}
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                padding: 14,
                borderTop: "1px solid rgba(15, 23, 42, 0.06)",
                background: "rgba(255,255,255,0.55)",
              }}
            >
              <Button
                block
                icon={<FaAngleLeft />}
                onClick={handleLogout}
                style={{
                  height: 44,
                  fontWeight: 600,
                }}
              >
                {!collapsed ? "Logout" : ""}
              </Button>
            </div>
          </Sider>
        )}

        {/* Mobile Drawer + Floating toggle */}
        {isMobile && (
          <>
            <Drawer
              placement="left"
              open={drawerVisible}
              onClose={() => setDrawerVisible(false)}
              width={280}
              bodyStyle={{ padding: 0 }}
            >
              <div style={{ padding: 18 }}>
                <Space align="center" size={12}>
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 14,
                      background:
                        "linear-gradient(135deg, rgba(47,84,235,1), rgba(47,84,235,0.55))",
                      boxShadow: "0 10px 24px rgba(47,84,235,0.25)",
                    }}
                  />
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      Webmaster
                    </Title>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Dashboard
                    </Text>
                  </div>
                </Space>
              </div>
              <Divider style={{ margin: 0, opacity: 0.6 }} />
              <div style={{ padding: 8 }}>{menu}</div>
              <div style={{ padding: 14 }}>
                <Button
                  block
                  icon={<FaAngleLeft />}
                  onClick={handleLogout}
                  style={{ height: 44, fontWeight: 600 }}
                >
                  Logout
                </Button>
              </div>
            </Drawer>

            <Button
              icon={<FaBars />}
              onClick={() => setDrawerVisible(true)}
              style={{
                position: "fixed",
                top: 16,
                left: 16,
                zIndex: 2000,
                width: 44,
                height: 44,
                borderRadius: 14,
                background: "rgba(255,255,255,0.85)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(15, 23, 42, 0.10)",
                boxShadow: "0 10px 24px rgba(15,23,42,0.10)",
              }}
            />
          </>
        )}

        {/* Main area */}
        <Layout style={{ background: "transparent" }}>
          {/* Top header */}
          <Header
            style={{
              position: "sticky",
              top: 0,
              zIndex: 10,

              // ✅ consistent margins
              margin: "12px 12px 0 0",

              // ✅ compact height
              height: 60,
              lineHeight: "60px",

              // ✅ proper background (not a huge white block)
              background: "rgba(255,255,255,0.88)",

              borderRadius: 16,
              border: "1px solid rgba(15, 23, 42, 0.08)",
              backdropFilter: "blur(10px)",
              boxShadow: "0 10px 24px rgba(15,23,42,0.06)",

              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",

              // ✅ reduce padding a bit
              padding: "0 14px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                minWidth: 0,
              }}
            >
              {!isMobile && (
                <Button
                  type="text"
                  onClick={() => setCollapsed((v) => !v)}
                  icon={<FaBars />}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 12,
                    border: "1px solid rgba(15, 23, 42, 0.10)",
                    background: "rgba(255,255,255,0.9)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
              )}

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  minWidth: 0,
                }}
              >
                <Title
                  level={5}
                  style={{
                    margin: 0,
                    lineHeight: 1.1,
                    fontSize: 18,
                    fontWeight: 800,
                    color: "rgba(15,23,42,0.92)",
                  }}
                >
                  {viewMeta[currentView].title}
                </Title>

                {!isMobile && viewMeta[currentView].subtitle && (
                  <Text
                    style={{
                      fontSize: 12,
                      color: "rgba(15,23,42,0.55)",
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {viewMeta[currentView].subtitle}
                  </Text>
                )}
              </div>
            </div>

            <Space size={10} align="center">
              <Dropdown
                overlay={userMenu}
                placement="bottomRight"
                trigger={["click"]}
              >
                <Button
                  type="text"
                  style={{
                    height: 36,
                    borderRadius: 12,
                    border: "1px solid rgba(15, 23, 42, 0.10)",
                    background: "rgba(255,255,255,0.9)",
                    padding: "0 10px",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  <Space size={8}>
                    <Avatar
                      size={24}
                      style={{ background: "rgba(15,23,42,0.18)" }}
                    >
                      W
                    </Avatar>
                    {!isMobile && (
                      <span
                        style={{
                          fontWeight: 700,
                          color: "rgba(15,23,42,0.85)",
                        }}
                      >
                        Webmaster
                      </span>
                    )}
                  </Space>
                </Button>
              </Dropdown>
            </Space>
          </Header>

          {/* Content shell */}
          <Content
            style={{
              padding: isMobile ? "12px" : "16px 16px 16px 0",
            }}
          >
            {/* Optional: stats strip / quick summary row (wire later) */}
            {/* <StatsRow currentView={currentView} /> */}
            {/* <DashboardStats /> */}
            <div
              style={{
                borderRadius: 18,
                background: "rgba(255,255,255,0.85)",
                border: "1px solid rgba(15, 23, 42, 0.06)",
                boxShadow: "0 12px 30px rgba(15,23,42,0.06)",
                padding: isMobile ? 12 : 16,
                minHeight: isMobile
                  ? "calc(100vh - 120px)"
                  : "calc(100vh - 120px)",
              }}
            >
              {renderView()}
            </div>
          </Content>
        </Layout>
      </Layout>
    </ConfigProvider>
  );
};

export default Dashboard;
